/**
 * Calendar display helpers.
 *
 * Pure functions used by the calendar UI (Week/Day/List/Month views in
 * `AppointmentsTab.jsx` and the V2 CalendarRoute). Hoisted out of the
 * 2400-line JSX block in Stage 3 of the Calendar refactor so the V2
 * CalendarRoute can render `<AppointmentsTab />` natively without
 * relying on the Dashboard-scope closures.
 *
 * Why these helpers don't live in `utils/dateFormat.js`:
 *   - `formatForDateTimeInput` produces the value format expected by
 *     `<input type="datetime-local">` (NO timezone, NO seconds). The
 *     existing `formatLocalDateTime` is for user-facing display only.
 *   - The colour helpers are pure JS and don't belong anywhere else
 *     yet — once Stage 4 of the refactor lands they may move to
 *     `utils/color.js` alongside other UI palette helpers.
 */

// ─── Date helpers ──────────────────────────────────────────────────

/**
 * Format a Date for an `<input type="datetime-local">` value.
 * Output shape: `YYYY-MM-DDTHH:MM` (no seconds, no timezone — the
 * browser implicitly treats this as local time).
 *
 * @param {Date} d
 * @returns {string}
 */
export const formatForDateTimeInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
};

// ─── Colour helpers ────────────────────────────────────────────────

const HEX_RE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

/**
 * Convert a 6-digit hex string (`#abcdef` or `abcdef`) into an
 * `{r, g, b}` integer triple. Returns a safe blue fallback for
 * non-matching inputs.
 */
export const hexToRgb = (hex) => {
  const result = HEX_RE.exec(hex || '');
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 59, g: 130, b: 246 }; // default blue (Tailwind blue-500)
};

/**
 * Convert a 6-digit hex string into a CSS `rgba()` string with the
 * given alpha. Returns a blue fallback for invalid input.
 */
export const hexToRgba = (hex, alpha) => {
  const result = HEX_RE.exec(hex || '');
  if (result) {
    return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
  }
  return `rgba(59, 130, 246, ${alpha})`;
};

/**
 * Return a darker version of `hexColor` (40% brightness) so that text
 * remains readable when laid over a light-tinted background of the
 * same hue. Falls back to gray-800 for unparseable input.
 */
export const getContrastTextColor = (hexColor) => {
  const result = HEX_RE.exec(hexColor || '');
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgb(${Math.floor(r * 0.4)}, ${Math.floor(g * 0.4)}, ${Math.floor(b * 0.4)})`;
  }
  return '#1F2937'; // Tailwind gray-800
};

/**
 * Chip text color: keeps dark colors as-is but darkens light ones
 * (e.g. the light-purple internal types) so text stays readable on
 * the chip's pale tinted background.
 */
export const getReadableChipTextColor = (hexColor) => {
  const result = HEX_RE.exec(hexColor || '');
  if (!result) return hexColor;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (luminance >= 0.6) {
    return `rgb(${Math.floor(r * 0.45)}, ${Math.floor(g * 0.45)}, ${Math.floor(b * 0.45)})`;
  }
  if (luminance >= 0.38) {
    // Mid-tone hues (e.g. #3B82F6) still miss 4.5:1 on a pale tint — darken gently.
    return `rgb(${Math.floor(r * 0.65)}, ${Math.floor(g * 0.65)}, ${Math.floor(b * 0.65)})`;
  }
  return hexColor;
};

// ─── Overlap layout helper ─────────────────────────────────────────

/**
 * For Google-Calendar-style overlap rendering: given a target
 * appointment and the full list of appointments on the same day,
 * returns the size and index of the overlap group so the caller can
 * compute width%/left% for each chip.
 *
 * @param {Object} targetApt  Appointment to position.
 * @param {Array}  dayAppointments  All appointments on the same day.
 * @returns {{ totalOverlapping: number, positionIndex: number }}
 */
export const getOverlapInfo = (targetApt, dayAppointments) => {
  const targetStart = new Date(targetApt.start_time);
  const targetEnd = new Date(targetApt.end_time);

  const overlapping = dayAppointments.filter((apt) => {
    const aptStart = new Date(apt.start_time);
    const aptEnd = new Date(apt.end_time);
    return aptStart < targetEnd && aptEnd > targetStart;
  });

  overlapping.sort((a, b) => {
    const startDiff = new Date(a.start_time) - new Date(b.start_time);
    if (startDiff !== 0) return startDiff;
    return String(a.id).localeCompare(String(b.id));
  });

  return {
    totalOverlapping: overlapping.length,
    positionIndex: overlapping.findIndex((apt) => apt.id === targetApt.id),
  };
};

// ─── Calendar grid helper ──────────────────────────────────────────

/**
 * Returns the 42 dates (6 rows × 7 columns) used to render the Month
 * view grid for `calendarDate`. The grid always starts on the Sunday
 * on-or-before the 1st of the month, which is how Google Calendar /
 * Outlook / Apple Calendar all render it. 42 cells guarantee the
 * grid is exactly 6 rows tall no matter how the month falls.
 *
 * @param {Date} calendarDate
 * @returns {Date[]}
 */
export const getCalendarDays = (calendarDate) => {
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);
  for (let i = 0; i < 42; i += 1) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

// ─── Appointment classification ────────────────────────────────────

const TELEHEALTH_POS_CODES = new Set(['02', '10']);

/**
 * `true` when the appointment is delivered via telehealth.  Three
 * independent signals are checked because we have legacy data that
 * predates each one being recorded consistently:
 *   1. POS code `02` (telehealth — other) or `10` (telehealth — home).
 *   2. Presence of a `google_meet_link` (older records).
 *   3. Explicit `telehealth_modality` field (newest path).
 *
 * @param {Object} appointment
 * @returns {boolean}
 */
export const isTelehealthAppointment = (appointment) => {
  if (!appointment) return false;
  if (TELEHEALTH_POS_CODES.has(appointment.pos_code)) return true;
  if (appointment.google_meet_link) return true;
  if (appointment.telehealth_modality) return true;
  return false;
};

// ─── Status / badge / billing helpers ──────────────────────────────

const STATUS_BADGE_COLORS = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-red-100 text-red-800',
  client_declined: 'bg-rose-100 text-rose-800',
  paid: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
  ready_for_secondary: 'bg-amber-100 text-amber-800',
  draft: 'bg-gray-100 text-gray-800',
  sent_to_supervisor: 'bg-blue-100 text-blue-800',
  pending_supervisor_signature: 'bg-blue-100 text-blue-800',
  sent_to_client: 'bg-indigo-100 text-indigo-800',
  sent_to_client_for_signature: 'bg-indigo-100 text-indigo-800',
  client_completed: 'bg-green-100 text-green-800',
  provider_signed: 'bg-purple-100 text-purple-800',
  supervisor_signed: 'bg-purple-100 text-purple-800',
  signed_ready_for_billing: 'bg-green-100 text-green-800',
  billed: 'bg-blue-100 text-blue-800',
  needs_corrections: 'bg-orange-100 text-orange-800',
  pending_signatures: 'bg-yellow-100 text-yellow-800',
  fully_signed: 'bg-green-100 text-green-800',
  completed: 'bg-emerald-100 text-emerald-800',
  payroll_approved: 'bg-teal-100 text-teal-800',
  payroll_approved: 'bg-teal-100 text-teal-800',
  locked: 'bg-gray-100 text-gray-700',
  // Mar 2026 — distinct chips for non-billable variants
  not_billable: 'bg-red-100 text-red-800',
  duplicate: 'bg-slate-200 text-slate-800 border border-slate-400',
};

/**
 * Tailwind-class background+text pair for a status badge. Falls back
 * to neutral gray for unknown statuses.
 */
export const getStatusBadgeColor = (status) =>
  STATUS_BADGE_COLORS[status] || 'bg-gray-100 text-gray-800';

const NON_BILLABLE_APPOINTMENT_TYPES = new Set([
  'block_time',
  'supervision',
  'vacation',
  'sick_day',
  'internal',
]);

/**
 * Returns the hex colour to use for the left-edge border of an
 * appointment chip based on its billing-pipeline status. Returns
 * `null` for non-billable appointment types so the caller can omit
 * the border entirely.
 *
 * @param {Object} appointment
 * @param {Array}  notesData         Optional. Notes to search for a
 *                                   linked note. Used only when no
 *                                   `notesByApptMap` is supplied.
 * @param {Map}    notesByApptMap    Optional. O(1) lookup
 *                                   `Map<appointment_id, note>`
 *                                   built once per render.
 */
export const getBillingStatusBorderColor = (
  appointment,
  notesData,
  notesByApptMap = null
) => {
  if (NON_BILLABLE_APPOINTMENT_TYPES.has(appointment.appointment_type)) {
    return null;
  }
  if (appointment.is_billable === false) {
    return '#9CA3AF'; // gray-400
  }

  let noteStatus = appointment.linked_note_status;
  if (!noteStatus && !appointment.has_linked_note) {
    const linkedNote = notesByApptMap
      ? notesByApptMap.get(appointment.id)
      : notesData?.find((n) => n.appointment_id === appointment.id);
    if (linkedNote) noteStatus = linkedNote.status;
  }

  if (!noteStatus && !appointment.has_linked_note) {
    return '#FBBF24'; // yellow-400 — no linked note yet
  }

  switch (noteStatus) {
    case 'paid':
    case 'claim_paid':
      return '#22C55E'; // green-500
    case 'denied':
    case 'claim_denied':
      return '#EF4444'; // red-500
    case 'needs_corrections':
      return '#A855F7'; // purple-500
    case 'billed':
    case 'claim_created':
    case 'submitted':
      return '#F97316'; // orange-500
    case 'signed_ready_for_billing':
      return '#14B8A6'; // teal-500
    default:
      return '#FBBF24'; // yellow-400 — draft / in-progress
  }
};

/**
 * Picks a representative hex colour for an appointment chip based on
 * the CPT code's category (and the code's custom colour override if
 * one exists). Falls back to keyword matching against
 * `service_type` / `category` for legacy appointments that don't
 * carry a real `cpt_code_id`.
 */
export const INTERNAL_TYPE_DEFAULT_COLOR = '#C4B5FD'; // light purple

export const getServiceTypeCategoryColor = (
  appointment,
  cptCodesData,
  cptByIdMap = null,
  internalTypesById = null
) => {
  if (appointment.appointment_type === 'block_time') {
    return '#b5e1d3'; // mint
  }

  // Internal / supervision appointments: use the managed type's settable
  // color (Org Settings → Internal Appointment Types), default light purple.
  if (
    appointment.internal_type_id ||
    appointment.appointment_type === 'internal' ||
    appointment.appointment_type === 'supervision'
  ) {
    if (internalTypesById) {
      const byId = appointment.internal_type_id
        ? internalTypesById.get(appointment.internal_type_id)
        : null;
      if (byId?.color) return byId.color;
      if (!appointment.internal_type_id) {
        for (const ty of internalTypesById.values()) {
          if (ty.slug === appointment.appointment_type && ty.color) return ty.color;
        }
      }
    }
    return INTERNAL_TYPE_DEFAULT_COLOR;
  }

  let cptCode = null;
  if (cptByIdMap && appointment.cpt_code_id) {
    cptCode = cptByIdMap.get(appointment.cpt_code_id);
  }
  if (!cptCode) {
    cptCode = cptCodesData?.find(
      (code) =>
        appointment.service_type?.includes(code.display_name || code.code) ||
        appointment.service_type?.includes(code.code) ||
        code.id === appointment.cpt_code_id
    );
  }
  if (cptCode?.color) return cptCode.color;

  const serviceType = (appointment.service_type || '').toLowerCase();
  const category = (cptCode?.category || '').toLowerCase();

  if (
    serviceType.includes('medication') ||
    serviceType.includes('med mgmt') ||
    category.includes('medication') ||
    serviceType.includes('99213') ||
    serviceType.includes('99214')
  ) {
    return '#14B8A6'; // teal-500
  }
  if (
    appointment.appointment_type === 'group_session' ||
    cptCode?.is_group_session ||
    serviceType.includes('group') ||
    category.includes('group')
  ) {
    return '#8B5CF6'; // purple-500
  }
  if (
    serviceType.includes('eval') ||
    serviceType.includes('assess') ||
    serviceType.includes('90791') ||
    serviceType.includes('90792') ||
    category.includes('evaluation') ||
    category.includes('assessment')
  ) {
    return '#F59E0B'; // amber-500
  }
  if (
    serviceType.includes('crisis') ||
    serviceType.includes('urgent') ||
    category.includes('crisis')
  ) {
    return '#F43F5E'; // rose-500
  }
  return '#3B82F6'; // blue-500 (default — outpatient therapy)
};
